import { formatNumberGivenValueType } from 'modules/productAnalytics/utils';
import React, { useState } from 'react';
import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { NUMBER, PERCENTAGE } from 'ui/card/constants';
import {
  ARROW_DIRECTION_DOWN,
  CollapsibleContent,
  CollapsibleContentArrow,
} from 'ui/collapsibleContent';
import { FlexSpacer } from 'ui/flexSpacer';
import { caption, colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { StyledHeatmapThumbnail, StyledUILink, Cell as TableCell } from '../Styled';

const ArrowButton = styled(IconButton)`
  cursor: pointer;
  flex: 0 0 auto;
  margin-right: ${spacing.quarter};
`;

const StyledCollapsibleContent = styled(CollapsibleContent)`
  grid-column: 3 / span 1;
  grid-row: 2 / span 1;
  grid-column-start: 1;
`;

const CollapsibleContentContainer = styled.div`
  padding: ${spacing.threeQuarter} ${spacing.plusHalf};
`;

const Caption = styled.div`
  ${caption}
  color: ${colors.textSecondary};
`;

const Cell = styled(TableCell)`
  display: grid;
  height: 100%;
`;

const GRID_TEMPLATE_COLUMNS = `
  grid-template-columns: minmax(${pxToRem(676)}, 1.5fr) 
                           minmax(${pxToRem(142)}, 1fr) 
                           minmax(${pxToRem(143)}, 1fr) 
                           minmax(${pxToRem(143)}, 1fr);
`;

const GRID_TEMPLATE_COLUMNS_FILTERED = `
  grid-template-columns: minmax(${pxToRem(488)}, 1.5fr) 
                           minmax(${pxToRem(205)}, 1fr) 
                           minmax(${pxToRem(205)}, 1fr) 
                           minmax(${pxToRem(205)}, 1fr);
`;

// Grid Table Layout
const GridTable = styled.div`
  display: grid;
  &[data-is-analytics-bot-filtering-enabled="true"] {
    ${GRID_TEMPLATE_COLUMNS_FILTERED}
  }
  &[data-is-analytics-bot-filtering-enabled="false"] {
    ${GRID_TEMPLATE_COLUMNS}
  }
  width: 100%;
`;

// Table Row
const TableRow = styled.div`
  display: contents; /* Keeps grid layout for rows */

  div {
    padding: ${spacing.threeQuarter} ${spacing.plusHalf} ${spacing.threeQuarter} ${spacing.threeQuarter};
  }

  div:first-child {
    padding-left: 0;
  }
  div:not(:first-child) {
    text-align: right;
  }
`;

type ExtendedTrackInfoProps = {
  img?: string;
  text?: string;
  trackingId: string;
  clickRate: number;
  uniqueClickRate: number;
  totalCount: number;
  uniqueCount: number;
  isTemplateLink: boolean;
};

type Props = {
  url: string;
  urlOnlyView: boolean;
  extendedTrackInfo: ExtendedTrackInfoProps[];
  isAnalyticsBotFilteringEnabled: boolean;
};

const UrlContent = ({
  url,
  isAnalyticsBotFilteringEnabled,
  urlOnlyView,
}: {
  url: string;
  isAnalyticsBotFilteringEnabled: boolean;
  urlOnlyView: boolean;
}) => (
  <StyledUILink
    data-url-only-view={urlOnlyView}
    data-is-analytics-bot-filtering-enabled={isAnalyticsBotFilteringEnabled}
    variant="monochrome"
    href={url}
    target="_blank"
  >
    {url}
  </StyledUILink>
);

const TemplateLinks = ({
  templateLinks,
  isAnalyticsBotFilteringEnabled,
}: {
  templateLinks: ExtendedTrackInfoProps[];
  isAnalyticsBotFilteringEnabled: boolean;
}) => (
  <React.Fragment>
    <Caption>Link Content in Template</Caption>
    <GridTable data-is-analytics-bot-filtering-enabled={isAnalyticsBotFilteringEnabled}>
      {templateLinks.map((info) => (
        <TableRow key={info.trackingId}>
          <div>
            {info.img ? <StyledHeatmapThumbnail src={info.img} alt="Image for link" /> : info.text}
          </div>
          <div>{formatNumberGivenValueType(PERCENTAGE, info.uniqueClickRate)}</div>
          <div>{formatNumberGivenValueType(NUMBER, info.totalCount)}</div>
          <div></div>
        </TableRow>
      ))}
    </GridTable>
  </React.Fragment>
);

const SnippetLinks = ({
  snippetLinks,
  isAnalyticsBotFilteringEnabled,
  urlOnlyView,
}: {
  snippetLinks: ExtendedTrackInfoProps[];
  isAnalyticsBotFilteringEnabled: boolean;
  urlOnlyView: boolean;
}) => (
  <React.Fragment>
    <Caption>Snippet ID</Caption>
    <GridTable data-is-analytics-bot-filtering-enabled={isAnalyticsBotFilteringEnabled}>
      {snippetLinks.map(({ trackingId, uniqueClickRate, totalCount }) => {
        const [snippetId] = (trackingId ?? '').split('-');
        return (
          <TableRow key={trackingId}>
            <div>
              <StyledUILink
                key={snippetId}
                data-url-only-view={urlOnlyView}
                data-is-analytics-bot-filtering-enabled={isAnalyticsBotFilteringEnabled}
                href={`/templates/snippet/${snippetId}/edit`}
                target="_blank"
              >
                {snippetId}
              </StyledUILink>
            </div>

            <div>{formatNumberGivenValueType(PERCENTAGE, uniqueClickRate)}</div>
            <div>{formatNumberGivenValueType(NUMBER, totalCount)}</div>
            <div></div>
          </TableRow>
        );
      })}
    </GridTable>
  </React.Fragment>
);

const CollapsibleInfo = ({
  isCollapsed,
  isAnalyticsBotFilteringEnabled,
  urlOnlyView,
  extendedTrackInfo,
}: {
  isCollapsed: boolean;
  isAnalyticsBotFilteringEnabled: boolean;
  urlOnlyView: boolean;
  extendedTrackInfo: ExtendedTrackInfoProps[];
}) => {
  const { templateLinks, snippetLinks } = extendedTrackInfo.reduce(
    (acc, info) => {
      if (info.isTemplateLink) {
        acc.templateLinks.push(info);
      } else {
        acc.snippetLinks.push(info);
      }
      return acc;
    },
    {
      templateLinks: [] as ExtendedTrackInfoProps[],
      snippetLinks: [] as ExtendedTrackInfoProps[],
    },
  );

  return (
    <StyledCollapsibleContent isCollapsed={isCollapsed} data-test="collapsible-url-cell">
      <CollapsibleContentContainer>
        {templateLinks.length > 0 && (
          <TemplateLinks
            isAnalyticsBotFilteringEnabled={isAnalyticsBotFilteringEnabled}
            templateLinks={templateLinks}
          />
        )}
        {snippetLinks.length > 0 && (
          <SnippetLinks
            isAnalyticsBotFilteringEnabled={isAnalyticsBotFilteringEnabled}
            urlOnlyView={urlOnlyView}
            snippetLinks={snippetLinks}
          />
        )}
      </CollapsibleContentContainer>
    </StyledCollapsibleContent>
  );
};

export const UrlCell = ({
  url,
  urlOnlyView,
  isAnalyticsBotFilteringEnabled,
  extendedTrackInfo = [],
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  if (!urlOnlyView) {
    return (
      <Cell>
        <FlexSpacer height="100%" alignItems="center" justifyContent="space-between">
          {url ? (
            <UrlContent
              urlOnlyView={urlOnlyView}
              isAnalyticsBotFilteringEnabled={isAnalyticsBotFilteringEnabled}
              url={url}
            />
          ) : (
            '[dynamic url]'
          )}
        </FlexSpacer>
      </Cell>
    );
  }

  return (
    <React.Fragment>
      <Cell style={{ paddingRight: 0 }}>
        <FlexSpacer height="100%" alignItems="center" justifyContent="space-between">
          {url ? (
            <UrlContent
              urlOnlyView={urlOnlyView}
              isAnalyticsBotFilteringEnabled={isAnalyticsBotFilteringEnabled}
              url={url}
            />
          ) : (
            '[dynamic url]'
          )}
          <ArrowButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            data-test="collapsible-arrow-button"
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <CollapsibleContentArrow
              isCollapsed
              arrowStart={ARROW_DIRECTION_DOWN}
              arrowEnd={180}
              width={16}
              height={16}
            />
          </ArrowButton>
        </FlexSpacer>
      </Cell>
      <CollapsibleInfo
        isCollapsed={isCollapsed}
        isAnalyticsBotFilteringEnabled={isAnalyticsBotFilteringEnabled}
        urlOnlyView={urlOnlyView}
        extendedTrackInfo={extendedTrackInfo}
      />
    </React.Fragment>
  );
};
